/**
 * This Wizard component is very similar to the one in shopping-pwa and mainly contains:
 * - A start and end date input/picker (datepicker)
 * - A guests and rooms picker
 * - A submit button
 * All of which are tracked. When the user chooses their dates/guests/rooms and clicks
 * submit, they get sent to HSR.
 *
 */
import * as React from "react";
import {
  FormFieldModel,
  GuestDialogProps,
  HiddenSearchDataProps,
  TypeaheadSelectionProps,
  WizardHotelPWAFlexComponent,
  WizardHotelPWAProps,
} from "./typings";
import { observer } from "mobx-react";
import { action, computed, observable } from "mobx";
import { QueryRoute } from "bernie-core";
import { ClientLogger } from "bernie-client";
import { Layer } from "bernie-view";
import { ViewLarge, ViewMedium, Viewport, ViewSmall } from "@shared-ui/viewport-context";
import { TypeaheadSelection } from "uitk-react-typeahead";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { WizardDialogTrigger } from "./components/WizardDialogTrigger";
import { DestinationField } from "./components/DestinationField";
import { GuestDialog, GuestField } from "./components/GuestField";
import { HiddenSearchData } from "./components/HiddenSearchData";
import { SubmitButton, SubmitButtonProps } from "./components/SubmitButton";
import { locKeys } from "./components/l10n";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedTrackEvent,
  sendImmediateTrackEvent,
} from "components/utility/analytics/FlexAnalyticsUtils";
import { SearchForm } from "./components/SearchForm";
import {
  getHSRFilterCode,
  getHSRFilters,
  getHSRStarRatingCode,
  getPropertyTypeCode,
} from "src/components/utility/ContextUtil";
import { Experiment, getExperimentBucket, isVariantEnabled } from "../../utility/experiment";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { DatePicker } from "./components/DatePicker";
import { UitkNewDatePickerTriggeredSelector } from "uitk-react-new-date-picker";
import { EssClientLogger } from "typeahead-client-logger";
import { StickyCTAMobile } from "./components/StickyCTAMobile";
import { IntentMediaWizardMapping } from "src/components/utility/Ads/IntentMedia/typings";
import { getNextFriday } from "src/components/utility/DateUtils";
import { withStores } from "stores";
import { classNames } from "uitk-react-utils";
import { searchButtonCopyL10n } from "components/flexComponents/WizardHotelPWA/components/SearchButtonCopyL10n";
import { RateCalendarFooter } from "components/flexComponents/WizardHotelPWA/components/RateCalendarFooter";

enum PageType {
  AIRPORT = "airport",
  POI = "point_of_interest",
}

enum StickWhenType {
  WILL_EXIT = "willExit",
  HAS_EXITED = "hasExited",
}

const SORT_DISTANCE_VALUE = "distance";

interface WizardHotelPWAState {
  startInvalidMessage: string;
  endInvalidMessage: string;
}

export class WizardHotelPWAComponent extends React.Component<WizardHotelPWAProps> {
  @observable
  private startDate = "";
  @observable
  private endDate = "";
  @observable
  private guestsVal = "";
  @observable
  private adults = ""; // Example: "2", "2,1,2" (represents each room's total adults)
  @observable
  private children = ""; // Example: "1_0, 1_7" (represents a child and their age)
  @observable
  private backTracking = {
    isProgrammatic: false,
    dialog: "",
  };

  @observable
  private destination = "";

  @observable
  private ttla = "";

  @observable
  private shortName = "";

  @observable
  private countryCode = "";

  @observable
  private regionId = "";

  @observable
  private selected = "";

  @observable
  private latLong = "";

  @observable
  private submitButtonClicked = false;

  @observable
  private destinationFieldProperties: FormFieldModel;

  @observable
  private shouldShowExpandedWizardInHsr = false;

  @observable
  private wizardContainerIsOutOfWindow = false;

  @observable
  private componentsWithJs = false;

  // will auto open the new date picker, given the right experiment and desktop
  @observable
  private newDatePickerIsOpen = false;

  private focusGuestPicker: React.RefObject<HTMLInputElement>;
  private wizardFormRef: React.RefObject<HTMLFormElement>;
  private wizardRef: React.RefObject<HTMLDivElement>;
  private destinationRef: React.RefObject<HTMLInputElement>;
  private essAnalyticsLogger: EssClientLogger;
  private showDestinationField = false;
  private isLargeScreenAndStickWhenWillExit: boolean;
  private isDestinationFieldExpEnabled: boolean;
  private shouldRouteToHsrFromCalendar = false;
  private isDesktop: boolean;
  private formatDate: (date: Date, options: any) => string;
  private formatText: (key: string, ...args: any[]) => string;

  public state: Readonly<WizardHotelPWAState> = {
    startInvalidMessage: "",
    endInvalidMessage: "",
  };

  constructor(props: WizardHotelPWAProps) {
    super(props);
    const { startDate, context, flexComponent } = props;
    const { formatText, formatDate } = this.props.l10n;
    this.formatDate = formatDate;
    this.formatText = formatText;

    this.isDesktop = !context?.deviceInformation?.mobile && !context?.deviceInformation?.tablet;
    this.isDestinationFieldExpEnabled = isVariantEnabled(context, Experiment.Flex_typeahead_in_wizard);
    this.showDestinationField = !!flexComponent.model.showDestinationField || this.isDestinationFieldExpEnabled;

    this.setInitialDates(startDate);
    this.destinationFieldProperties = SearchForm.HotelOnly;

    // if there is no location search context, there should be a destination field otherwise you can't search
    if (!context.searchContext.location) {
      this.showDestinationField = true;
    } else {
      this.destination = context.searchContext.location.extendedName;
      this.regionId = context.searchContext.location.id;
    }

    this.newDatePickerIsOpen = Boolean(this.isDesktop && flexComponent.model.desktopAutoOpenCalendar);

    this.focusGuestPicker = React.createRef();
    this.wizardFormRef = React.createRef();
    this.wizardRef = React.createRef();

    const isMobileWizardDatesToSRPExpEnabled = isVariantEnabled(
      context,
      Experiment.Flex_Blossom_Mobile_Wizard_Dates_To_SRP
    );
    // It is probably not storefront if there's no destination field but we are also testing the dest field on HTG
    // TODO: Using presence of destination field is not a good indicator for a non-storefront page, let's change this
    if ((!this.showDestinationField || this.isDestinationFieldExpEnabled) && !isMobileWizardDatesToSRPExpEnabled) {
      this.shouldRouteToHsrFromCalendar = true;
    }

    if (isVariantEnabled(context, Experiment.PWA_ESS_Feedback_Loop_Enable)) {
      this.essAnalyticsLogger = new EssClientLogger(
        props.analytics,
        ClientLogger,
        this.destination,
        context?.experimentContext?.experiments
      );
    }
  }

  /**
   * Sets the initial wizard interval that our users will start and end their trip.
   * @param context - Required to pass to isVariantEnabled to access the experiments
   * @param startDate - The passed start date that the user will by default start
   *  their trip dates with
   */
  private setInitialDates(startDate = new Date()): void {
    // If HTG and Desktop, don't put dates
    // TODO: Using presence of destination field is not a good indicator for a non-storefront page, let's change this
    if ((!this.showDestinationField || this.isDestinationFieldExpEnabled) && this.isDesktop) {
      return;
    }

    // Test winner (https://tnl.prod.expedia.com/#experiment-detail/32463): interval 30 days in the future
    // We only want for non-storefront
    // If affinity page not apply interval 30 days in the future
    // TODO: Using presence of destination field is not a good indicator for a non-storefront page, let's change this
    if (
      (!this.showDestinationField || this.isDestinationFieldExpEnabled) &&
      !this.isAffinityPageRegExMatch(this.props.context)
    ) {
      startDate.setDate(startDate.getDate() + 30);
    }

    //Only page affinity has check-in and check-out date 2 weeks from today
    const today = new Date(startDate);
    const checkInDate = this.isAffinityPageRegExMatch(this.props.context) ? getNextFriday(today) : today;
    const checkOutDate = new Date(checkInDate.getTime() + 24 * 60 * 60 * 1000);
    this.startDate = this.formatDate(checkInDate, { raw: "yyyy-MM-dd" });
    this.endDate = this.formatDate(checkOutDate, { raw: "yyyy-MM-dd" });
  }

  private removeAutoOpenBehaviorFromDatePicker = (flexComponent: WizardHotelPWAFlexComponent): void => {
    const model = flexComponent && flexComponent.model;
    const stickWhen = model && model.stickWhen;

    if (!model || !stickWhen || stickWhen !== StickWhenType.WILL_EXIT) {
      return;
    }

    // For DX pages only
    this.newDatePickerIsOpen = false;
  };

  public componentDidMount() {
    window.addEventListener("popstate", this.listenToBackButton);
    window.addEventListener("scroll", this.onScroll);
    this.removeAutoOpenBehaviorFromDatePicker(this.props.flexComponent);

    /**
     * Values set if there is rehydratation
     */
    this.adults = "2";
    this.guestsVal = this.formatText(locKeys.singleRoomMultipleGuests, this.adults);
    this.componentsWithJs = true;
  }

  public componentWillUnmount() {
    window.removeEventListener("popstate", this.listenToBackButton);
    window.removeEventListener("scroll", this.onScroll);
  }

  private listenToBackButton = () => {
    //wizard.hotel.back-button
    const datepickerTrackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel",
      action: Action.BACK_BUTTON,
    };

    //wizard.hotel.guests.back-button
    const guestsTrackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel.guests",
      action: Action.BACK_BUTTON,
    };

    if (!this.backTracking.isProgrammatic && this.backTracking.dialog !== "") {
      const trackingInfo =
        this.backTracking.dialog === WizardDialogTrigger.GUESTPICKER ? guestsTrackingInfo : datepickerTrackingInfo;
      sendDelayedTrackEvent(trackingInfo, this.props.analytics);
      this.backTracking.dialog = "";
    }
    this.backTracking.isProgrammatic = false;
  };

  public openDestinationTypeaheadDialog = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      action: Action.CLICK,
      rfrr: `hotel.destination.open`,
      linkName: "Destination dialog action",
    };
    sendDelayedTrackEvent(trackingInfo, this.props.analytics);
    if (this.props.routeStore) {
      this.props.routeStore.updateSearch({ pwaDialog: "destination" });
    }
  };

  public getSortFilterValue = (context: ExtendedContextStore) => {
    const { searchContext } = context;
    const isAirport = searchContext.location && searchContext.location.type === PageType.AIRPORT;
    const isPOI = searchContext.location && searchContext.location.type === PageType.POI;

    return isAirport || isPOI ? SORT_DISTANCE_VALUE : "";
  };

  @action
  public updateDestinationField = (selection: TypeaheadSelection) => {
    const model = this.props.flexComponent.model;
    const regionName = model.view === "expando" ? `.${this.regionId}` : "";
    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      action: Action.CLICK,
      rfrr: `hotel.destination.change${regionName}`,
      linkName: "Destination dialog action",
    };
    sendDelayedTrackEvent(trackingInfo, this.props.analytics);
    const { data, term } = selection as TypeaheadSelectionProps;
    const { regionId, regionNames, selected, location, hierarchyInfo } = data;

    this.destination = term;
    this.regionId = regionId || "";
    this.shortName = regionNames?.shortName;
    this.selected = selected || "";
    this.latLong = location?.lat && location?.long ? `${data.location.lat},${data.location.long}` : "";
    this.ttla = hierarchyInfo?.airport?.airportCode;
    this.countryCode = data?.hierarchyInfo?.country?.isoCode2;
  };

  @action
  public setMultiPropParam = (elements: HTMLFormControlsCollection) => {
    for (const element of elements) {
      if (element.id === "multiProp") {
        element.setAttribute("value", "true");
      }
    }
  };

  @action
  public clickDatePicker = (type: UitkNewDatePickerTriggeredSelector) => {
    const datepickerRfrrType = type === "start" ? "check-in" : "check-out";

    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      action: Action.CLICK,
      rfrr: `hotel.${datepickerRfrrType}`,
      linkName: "Datepicker dialog action",
    };

    sendDelayedTrackEvent(trackingInfo, this.props.analytics);

    if (this.props.routeStore) {
      this.props.routeStore.updateSearch({ pwaDialog: `datepicker-${type}` });
    }
    this.backTracking.dialog =
      type === "start" ? WizardDialogTrigger.DATEPICKERSTART : WizardDialogTrigger.DATEPICKEREND;
  };

  @action
  public closeDialog = (dialogType?: WizardDialogTrigger) => {
    if (this.props.routeStore) {
      this.props.routeStore.goBack();
    }

    let focus;
    switch (dialogType) {
      case WizardDialogTrigger.GUESTPICKER:
        focus = this.focusGuestPicker;
        break;
    }

    if (focus) {
      const focusInput = focus.current;
      if (focusInput && typeof focusInput.focus === "function") {
        focusInput.focus();
      }
    }
    this.backTracking.isProgrammatic = true;
    this.backTracking.dialog = "";
  };

  @computed
  get hotelRegionId() {
    return this.regionId;
  }

  @computed
  get totalTravelers() {
    const adultsArr = this.adults.split(",");
    let totalTravelers = this.children === "" ? 0 : this.children.split(",").length;
    adultsArr.forEach((el) => (totalTravelers += Number(el)));

    return totalTravelers;
  }

  get totalRooms() {
    return this.adults.split(",").length;
  }

  @computed
  get totalAdults() {
    const adultsArr = this.adults.split(",");
    let totalAdults = 0;
    adultsArr.forEach((el) => (totalAdults += Number(el)));

    return totalAdults;
  }

  @computed
  get totalChildren() {
    return this.children === "" ? 0 : this.children.split(",").length;
  }

  @computed
  get cityName() {
    return this.shortName;
  }

  @computed
  get country() {
    return this.countryCode;
  }

  @computed
  get travelStartDate() {
    return new Date(this.startDate);
  }

  @computed
  get travelEndDate() {
    return new Date(this.endDate);
  }

  @action
  public clickGuests = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      action: Action.CLICK,
      rfrr: "hotel.guests.open",
      linkName: "Guests dialog action",
    };

    sendDelayedTrackEvent(trackingInfo, this.props.analytics);
    if (this.props.routeStore) {
      this.props.routeStore.updateSearch({ pwaDialog: "guestdialog" });
    }
    this.backTracking.dialog = WizardDialogTrigger.GUESTPICKER;
  };

  @action
  public saveGuests = (adults: string, children: string, displaySummary?: string) => {
    this.adults = adults;
    this.children = children;

    if (displaySummary) {
      this.guestsVal = displaySummary;
    }

    this.closeDialog(WizardDialogTrigger.GUESTPICKER);
  };

  @action
  public closeGuestPicker = () => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel.guests.close",
      action: Action.CLICK,
      linkName: "Guests dialog action",
    };

    sendDelayedTrackEvent(trackingInfo, this.props.analytics);
    this.closeDialog(WizardDialogTrigger.GUESTPICKER);
  };

  @action
  public onScroll = () => {
    const wizardWasOutOfWindow = this.wizardContainerIsOutOfWindow;
    if (this.wizardRef && this.wizardRef.current) {
      const currentWizardRef = this.isLargeScreenAndStickWhenWillExit
        ? this.wizardRef.current.getBoundingClientRect().top
        : this.wizardRef.current.getBoundingClientRect().bottom;
      this.wizardContainerIsOutOfWindow = currentWizardRef < 0;
    }

    // this prevent the new date picker to stay opened when switching between sticky/non-sticky wizard
    if (wizardWasOutOfWindow !== this.wizardContainerIsOutOfWindow) {
      this.newDatePickerIsOpen = false;
    }
  };

  /**
   * Switch the open state of the DatePicker
   */
  public toggleDatePicker() {
    this.newDatePickerIsOpen = !this.newDatePickerIsOpen;
  }

  /**
   * Callback when the DatePicker is closed.
   */
  @action
  public onDatePickerClose() {
    this.toggleDatePicker();
  }

  /**
   * Callback when the Datepicker is click open.
   * @param dateType Start or End date
   */
  @action
  public onDatePickerOpen = (dateType: UitkNewDatePickerTriggeredSelector) => {
    this.toggleDatePicker();

    const rfrr = dateType === UitkNewDatePickerTriggeredSelector.START ? "check-in" : "check-out";
    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      action: Action.CLICK,
      rfrr: `hotel.${rfrr}`,
      linkName: "Datepicker dialog action",
    };

    sendDelayedTrackEvent(trackingInfo, this.props.analytics);
  };

  /**
   * Callback when the Datepicker is dismissed, it should close the mobile dialog
   * and change the state of the visibility of the desktop component
   * @param isMobile Whether the view is mobile or not
   * @param noUpdate Whether the view requires update or not
   */
  public onDatePickerDismiss = (isMobile: boolean, noUpdate?: boolean) => {
    if (!noUpdate) {
      this.toggleDatePicker();
    }

    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel.close",
      action: Action.CLICK,
      linkName: "Datepicker dialog action",
    };

    sendDelayedTrackEvent(trackingInfo, this.props.analytics);
  };

  /**
   * Callback when the DatePicker is submitted.
   * @param startDate Selected Start Date
   * @param endDate Selected End  Date
   * @param isMobile Whether the device is mobile or not
   */
  public onDatePickerSubmit = (startDate: Date, endDate: Date, isMobile: boolean = false) => {
    const trackingInfo: FlexTrackingInfo = {
      moduleName: this.getModuleNameForTracking(),
      rfrr: "hotel.done-btn",
      action: Action.CLICK,
      linkName: "Datepicker Done action",
    };
    sendImmediateTrackEvent(trackingInfo, this.props.analytics);

    if (startDate || endDate) {
      this.startDate = this.formatDate(startDate, { raw: "yyyy-MM-dd" });
      this.endDate = this.formatDate(endDate, { raw: "yyyy-MM-dd" });
      this.validateAndGetFormErrors();
    }

    if (isMobile && this.shouldRouteToHsrFromCalendar) {
      // When tnl 37559 in bucket 0 and mobile device, when selecting dates it should send to HSR directly
      this.shouldShowExpandedWizardInHsr = true;
      // Using setTimeout to add form submission to a "queue" in order to wait for dates to update
      setTimeout(() => {
        if (this.wizardFormRef && this.wizardFormRef.current && this.validateAndGetFormErrors().length === 0) {
          this.wizardFormRef.current.submit();
        } else {
          this.props.routeStore.closeDialog();
        }
      }, 0);
    } else {
      // Close dialog when in mobile and stop showing datepicker in all devices
      if (isMobile) {
        this.props.routeStore.closeDialog();
      }
      this.toggleDatePicker();
    }
  };

  private shouldSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    this.submitButtonClicked = true;
    if (this.validateAndGetFormErrors().length > 0) {
      e.preventDefault();
    } else {
      this.setMultiPropParam(e.currentTarget.elements);
      const intentMediaMapping: IntentMediaWizardMapping = {
        lob: "wizard-hotel-pwa",
        legacyWizardState: this,
      };
      window.dispatchEvent(new CustomEvent("IntentMediaSubmit", { detail: intentMediaMapping }));
    }
  };

  public render() {
    const flexComponent = this.props.flexComponent;
    const pageHeading = this.props.compositionStore?.composition?.pageHeading;
    const pageSubHeading = this.props.compositionStore?.composition?.pageSubHeadline;
    const expandoClasses = flexComponent.model.view === "expando" ? "elevation-4 all-x-padding-three" : "";
    const adults = this.adults || "2";

    const guestDialogProps: GuestDialogProps = {
      adults,
      children: this.children,
      moduleName: this.getModuleNameForTracking(),
      onDismiss: this.closeGuestPicker,
      onSave: this.saveGuests,
      l10n: this.props.l10n,
    };
    const mainTitleWizardClassNames = "uitk-cell s-cell-1-1 m-cell-3-4 l-cell-3-4 xl-cell-3-4";

    const WizardForm = this.WizardForm;

    return (
      <div
        id={flexComponent.id}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
        className={`WizardHotelPWA uitk-cell ${expandoClasses}`}
        ref={this.wizardRef}
      >
        <div className="WizardHotelPWA-wrapper">
          {flexComponent.model.includePageHeading && (
            <h1 className={`${mainTitleWizardClassNames} uitk-type-600 titlePage`}>{pageHeading}</h1>
          )}
          {flexComponent.model.includePageSubHeading && (
            <h2
              className={`${mainTitleWizardClassNames} uitk-type-400 uitk-type-regular s-b-padding-half m-b-padding-three l-b-padding-three xl-b-padding-three m-t-padding-one l-t-padding-one xl-t-padding-one subTitlePage`}
            >
              {pageSubHeading}
            </h2>
          )}
          <Viewport>
            <ViewSmall>
              <WizardForm isMobile={true} isTablet={true} />
            </ViewSmall>
            <ViewMedium>
              <WizardForm isMobile={false} isTablet={true} />
            </ViewMedium>
            <ViewLarge>
              <WizardForm isMobile={false} isTablet={false} />
            </ViewLarge>
          </Viewport>
          <QueryRoute query={{ pwaDialog: "guestdialog" }}>
            <Layer id="guestpicker-dialog">
              <GuestDialog {...guestDialogProps} />
            </Layer>
          </QueryRoute>
        </div>
      </div>
    );
  }

  // Not its own component as it is not yet known what the best way to refactor the wizard is yet.
  // We need both isMobile and isTablet, because the sticky wizard is for tablet and desktop, and the new
  // date picker is for desktop only.
  private WizardForm = observer(({ isMobile, isTablet }: { isMobile: boolean; isTablet: boolean }) => {
    const { context, flexComponent, rateCalendar } = this.props;

    const hsrFilterCode = getHSRFilterCode(context);
    const hsrFilters = getHSRFilters(context);
    const hsrStarRatingCode = getHSRStarRatingCode(context);
    const propertyType = getPropertyTypeCode(context);

    const hiddenSearchDataProps: HiddenSearchDataProps = {
      langId: context.langId,
      children: this.children,
      adults: this.adults,
      endDate: this.endDate,
      startDate: this.startDate,
      regionId: this.regionId,
      selected: this.selected,
      latLong: this.latLong,
      expandForm: this.shouldShowExpandedWizardInHsr,
      hsrFilterCode,
      hsrFilters,
      starRating: hsrStarRatingCode,
      sort: this.getSortFilterValue(context),
      propertyType,
      ttla: this.ttla,
    };

    //Logic to determine if the wizard should be sticky
    const isSticky =
      this.wizardContainerIsOutOfWindow && !isMobile && !!this.props.flexComponent.model.showStickyWizard;
    const isStickyMobileEnabled = isMobile && !!this.props.flexComponent.model.showStickyWizard;

    const model = flexComponent && flexComponent.model;
    const stickWhen = model && model.stickWhen;
    const showStickyCTA = model && model.showStickyCTA;

    const isLargeScreen = !(isMobile || isTablet);

    this.isLargeScreenAndStickWhenWillExit = isLargeScreen && stickWhen === StickWhenType.WILL_EXIT;

    let guestFieldClassNames = "uitk-cell s-cell-1-1 m-cell-1-3 l-cell-1-3 xl-cell-1-3";
    let submitButtonClassNames = "uitk-cell s-cell-1-1 m-cell-1-6 l-cell-1-6 xl-cell-1-6";

    const mainFormWizardClassNames = () => {
      let className = "uitk-cell s-cell-1-1 m-cell-1-1 l-cell-1-1 xl-cell-1-1";

      if (isSticky) {
        className = "sticky";
      }

      if (isSticky && this.isLargeScreenAndStickWhenWillExit) {
        className += " stickyWillExit";
      }

      if (this.isLargeScreenAndStickWhenWillExit) {
        className += " absolute";
      }

      return className;
    };

    if (this.showDestinationField) {
      guestFieldClassNames = "uitk-cell s-cell-1-1 m-cell-1-4 l-cell-1-6 xl-cell-1-6";
      submitButtonClassNames = "uitk-cell s-cell-1-1 m-cell-1-4 l-cell-1-6 xl-cell-1-6";
    }

    const stickyCTA = this.isAffinityPageRegExMatch(context)
      ? this.formatText(locKeys.stickyWizardAffinityTitle)
      : this.formatText(locKeys.stickyWizardTitle);
    const wizardFormTestId = isSticky ? "wizard-sticky" : "wizard";
    const mainTitleWizardClassNames = "uitk-cell s-cell-1-1 m-cell-3-4 l-cell-3-4 xl-cell-3-4";

    // showStickyCTA is a new field in FM and should have a default value of undefined
    const shouldShowStickyCTA = isSticky && (showStickyCTA || showStickyCTA === undefined);
    const setDestinationRef = (element: React.RefObject<HTMLInputElement>) => (this.destinationRef = element);
    const isWizardRedesignEnabled = isVariantEnabled(context, Experiment.Flex_Blossom_Wizard_Redesign);
    const innerFormContainerClassNames = React.useMemo(
      () =>
        classNames("uitk-grid all-x-gutter-three", {
          "all-b-padding-three": isWizardRedesignEnabled,
          "all-y-padding-three": !isWizardRedesignEnabled,
          "all-r-padding-three centerWizard": this.isLargeScreenAndStickWhenWillExit,
          "all-y-gutter-three": !this.isLargeScreenAndStickWhenWillExit,
        }),
      [this.isLargeScreenAndStickWhenWillExit, isWizardRedesignEnabled]
    );

    const isGuestsFieldDesktopVisible = !isWizardRedesignEnabled || (isWizardRedesignEnabled && !isMobile);

    const submitButtonProps: SubmitButtonProps = {
      moduleName: this.getModuleNameForTracking(),
      submitButtonText: this.getSubmitButtonText(),
      rfrr: "hotel.search",
    };

    const datePickerProps = {
      startDate: this.startDate,
      endDate: this.endDate,
      isOpen: this.newDatePickerIsOpen,
      onOpen: this.onDatePickerOpen,
      onDismiss: this.onDatePickerDismiss,
      onSubmit: this.onDatePickerSubmit,
      startInvalidMessage: this.state.startInvalidMessage,
      endInvalidMessage: this.state.endInvalidMessage,
    };
    const rateCalendarProps = rateCalendar.isExperimentEnabled
      ? {
          datePickerContent: rateCalendar.datePickerContent,
          footerHtml: <RateCalendarFooter {...rateCalendar.footerHtmlProps} />,
          footerText: rateCalendar.footerText,
        }
      : {};

    return (
      <>
        {isStickyMobileEnabled && (
          <StickyCTAMobile
            isSticky={this.wizardContainerIsOutOfWindow}
            moduleName={this.getModuleNameForTracking()}
            submitButtonText={this.formatText(locKeys.stickyWizardCallToAction)}
            scrollTarget={this.wizardFormRef}
          />
        )}
        <form
          noValidate={true}
          action="/Hotel-Search"
          onSubmit={this.shouldSubmit}
          ref={this.wizardFormRef}
          autoComplete="off"
          className={mainFormWizardClassNames()}
          data-testid={wizardFormTestId}
        >
          {isWizardRedesignEnabled && (
            <UitkLayoutFlex
              alignItems="center"
              justifyContent="space-between"
              className="uitk-grid uitk-cell all-y-padding-three"
            >
              {isMobile && (
                <UitkLayoutFlexItem grow={1}>
                  <div>
                    <GuestField
                      guestsVal={this.guestsVal}
                      open={this.clickGuests}
                      focusInput={this.focusGuestPicker}
                      invalid={this.validate("travelers")}
                      componentWithJs={this.componentsWithJs}
                      isMobile={isMobile}
                      context={context}
                    />
                  </div>
                </UitkLayoutFlexItem>
              )}
            </UitkLayoutFlex>
          )}
          <HiddenSearchData {...hiddenSearchDataProps} />
          {shouldShowStickyCTA && <h1 className={`${mainTitleWizardClassNames} uitk-type-500`}>{stickyCTA}</h1>}
          <div className={innerFormContainerClassNames}>
            {this.showDestinationField && (
              <div className="uitk-cell s-cell-1-1 m-cell-1-1 l-cell-1-2 xl-cell-1-3">
                <DestinationField
                  setDestinationRef={setDestinationRef}
                  formFieldModel={this.destinationFieldProperties}
                  openDialog={this.openDestinationTypeaheadDialog}
                  closeDialog={this.closeDialog}
                  updateDestinationField={this.updateDestinationField}
                  locale={context.locale}
                  guid={context.guid}
                  siteId={context.site.id.toString()}
                  invalid={this.validate("destination")}
                  destination={this.destination}
                  essAnalyticsLogger={this.essAnalyticsLogger}
                  enableGoogleAdapter={!this.isDestinationFieldExpEnabled}
                  enableEgta={context.locale === "en_US" && context.site?.id in ["1", "70201", "70301", "80001"]}
                />
              </div>
            )}
            <DatePicker {...datePickerProps} {...rateCalendarProps} />
            {isGuestsFieldDesktopVisible && (
              <div className={guestFieldClassNames}>
                <GuestField
                  guestsVal={this.guestsVal}
                  open={this.clickGuests}
                  focusInput={this.focusGuestPicker}
                  invalid={this.validate("travelers")}
                  componentWithJs={this.componentsWithJs}
                  isMobile={isMobile}
                  context={context}
                />
              </div>
            )}
            <div className={submitButtonClassNames}>
              <SubmitButton {...submitButtonProps} />
            </div>
          </div>
        </form>
        {this.isLargeScreenAndStickWhenWillExit && <form className="all-t-padding-twelve all-b-padding-six" />}
      </>
    );
  });

  private validate(key: string): string {
    const checkInDate = new Date(this.startDate);
    const checkOutDate = new Date(this.endDate);
    const destination = this.destination;

    if (key === "startDate") {
      if (!this.isValidDate(checkInDate)) {
        return this.formatText(locKeys.invalidFromDate);
      }
    }

    if (key === "endDate") {
      if (!this.isValidDate(checkOutDate)) {
        return this.formatText(locKeys.invalidToDate);
      }

      if (this.isValidDate(checkInDate)) {
        const maxEndDate = new Date(checkInDate!.getFullYear(), checkInDate!.getMonth(), checkInDate!.getDate() + 28);

        if (checkOutDate < checkInDate) {
          return this.formatText(locKeys.invalidDateRangeEndBeforeStart);
        }

        if (checkOutDate > maxEndDate) {
          return this.formatText(locKeys.invalidDateRangeEndOverLimit);
        }
      }
    }

    if (key === "destination" && destination === "" && this.submitButtonClicked) {
      return this.formatText(locKeys.invalidDestination);
    }

    return "";
  }

  public validateAndGetFormErrors = (event?: React.FormEvent<HTMLFormElement>) => {
    const errors = [];
    const startDateError = this.validate("startDate");
    const endDateError = this.validate("endDate");
    const destinationError = this.validate("destination");

    this.setState({
      startInvalidMessage: startDateError,
      endInvalidMessage: endDateError,
    });

    if (this.essAnalyticsLogger && "function" === typeof this.essAnalyticsLogger.logWizardSubmitEvent) {
      this.essAnalyticsLogger.logWizardSubmitEvent();
    }

    if (startDateError) {
      errors.push(startDateError);
    }
    if (endDateError) {
      errors.push(endDateError);
    }
    if (destinationError) {
      this.destinationRef.current?.focus();
      errors.push(destinationError);
    }

    if (errors.length > 0 && event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }

    return errors;
  };

  private isValidDate(date: Date) {
    return date && date.toString() !== "Invalid Date";
  }

  private getModuleNameForTracking = () => {
    // append sticky to the module name if needed
    // doesn't look at the device information since you can't interact with the wizard it if you don't see it.
    return this.wizardContainerIsOutOfWindow
      ? `${this.props.flexComponent.name}-sticky`
      : this.props.flexComponent.name;
  };

  private isAffinityPageRegExMatch = (context: ExtendedContextStore): boolean => {
    const affinityPathRegEx = /^(.*?(\HotelsAffinity\b)[^$]*)$/;

    return affinityPathRegEx.test(context.pageId);
  };

  private getSubmitButtonText(): string {
    // Uses bucket given by Blossom_Mab_Wizard_Search_Button_Copy experiment (or defaults to 0 which is control)
    // More info about this multi-armed bandit experiment here: https://confluence.expedia.biz/display/~knam/Running+MAB-type+Experiments+in+Blossom+Landing+Pages
    const mabBucket = getExperimentBucket(this.props.context, Experiment.Blossom_Mab_Wizard_Search_Button_Copy);

    return this.formatText(searchButtonCopyL10n[mabBucket] || locKeys.submitButton);
  }
}

export const WizardHotelPWA = withStores(
  "analytics",
  "routeStore",
  "context",
  "compositionStore",
  "wizardState",
  "rateCalendar"
)(observer(WizardHotelPWAComponent));

export default WizardHotelPWA;
